import { Menu } from '@szhsin/react-menu';
import type { FC, HTMLProps } from 'react';
import { useRef, useState, useCallback, useEffect } from 'react';
import { Navigate } from 'react-router-dom';

import {
    Container,
    NavigationContainer,
    NavigationBackground,
    MainContentContainer,
    Header,
    HeaderRightContainer,
    Main,
    MenuOpener,
    StyledMenuButton,
    StyledMenuItem,
} from './styled';

import { ComponentIDS } from '../../../constants/ids';
import { useAuthContext } from '../../../context/auth';
import { useNavigationContext } from '../../../context/navigation';
import { useClickOutside } from '../../../hooks/useClickOutside';
import { useIsMobileViewport } from '../../../hooks/useIsMobileViewport';
import { useScrollLock } from '../../../hooks/useScrollLock';
import { useSearchUrl } from '../../../hooks/useSearchUrl';
import { Avatar } from '../../Avatar';
import { Icon, IconName } from '../../Icon';
import { Loading } from '../../Loading';
import { Navigation } from '../../Navigation';
import { Search } from '../../Search';
import { Text } from '../../Typography/Text';

type IPrivateLayoutProps = Omit<HTMLProps<HTMLDivElement>, 'ref'>;

export const PrivateLayout: FC<IPrivateLayoutProps> = ({ children }) => {
    const { profileUrl } = useNavigationContext();
    const [isMenuOpen, setMenuOpen] = useState(false);
    const isMobileViewport = useIsMobileViewport();
    const {
        handleUserSignOff,
        user,
        isUserFetching,
        accessToken,
        refreshToken,
        signInPath,
    } = useAuthContext();
    const [initialFetchDone, setInitialFetchDone] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (user && !isUserFetching) {
            setInitialFetchDone(true);
        }
    }, [isUserFetching, user]);

    const closeMenu = () => {
        setMenuOpen(false);
    };

    const onSignOffHandler = useCallback(() => {
        handleUserSignOff();
    }, [handleUserSignOff]);

    useClickOutside(ref, closeMenu);
    useScrollLock({ isLocked: isMenuOpen });

    const onSearch = useSearchUrl();

    if (!initialFetchDone && isUserFetching) {
        return <Loading />;
    }

    if (!accessToken && !refreshToken) {
        return (
            <Navigate
                to={{
                    pathname: signInPath ?? undefined,
                }}
            />
        );
    }

    return (
        <Container>
            {(isMenuOpen || !isMobileViewport) && (
                <NavigationContainer ref={ref}>
                    <Navigation onClose={closeMenu} />
                </NavigationContainer>
            )}
            {isMenuOpen && isMobileViewport && <NavigationBackground />}
            <MainContentContainer>
                <Header>
                    <MenuOpener
                        onClick={() => setMenuOpen((prevState) => !prevState)}
                    >
                        <Icon iconName={IconName.MENU} />
                    </MenuOpener>
                    {/* Provide ID because of ReactPortal */}
                    <HeaderRightContainer id={ComponentIDS.HEADER}>
                        <Menu
                            menuButton={() => (
                                <StyledMenuButton>
                                    <Avatar user={user} />
                                </StyledMenuButton>
                            )}
                        >
                            <StyledMenuItem href={profileUrl ?? ''}>
                                <Icon iconName={IconName.PROFILE} />
                                <Text>View Profile</Text>
                            </StyledMenuItem>
                            <StyledMenuItem onClick={onSignOffHandler}>
                                <Icon iconName={IconName.SIGN_OUT} />
                                <Text>Sign out</Text>
                            </StyledMenuItem>
                        </Menu>

                        <Search onSubmit={onSearch} dimmed />
                    </HeaderRightContainer>
                </Header>
                <Main>{children}</Main>
            </MainContentContainer>
        </Container>
    );
};
